import SelectorEngine from '../../../utils/dom/selector-engine';
import { Events } from '../../../utils/dom/html-events';
import { ClassNames } from '../../../utils/dom/css-classes';
import Request, { ApiMode } from '../../../utils/request';
import EventHandler from '../../../utils/dom/event-handler';
import FormGenerator, { FormTypes } from '../../../components/FormGenerator';
import Modal from '../../../../client/components/Modal';
import { scrollToTop } from '../../../utils/misc';
import Manipulator from '../../../utils/dom/manipulator';
import { startLoadingAnimation, stopLoadingAnimation } from '../../../utils/dom/loading';

const SELECTOR_WRAPPER = '.p-accreditation-renewal';
const SELECTOR_MODAL = '.js-accreditation-renewal-modal';

const SELECTOR_SPLASH = '.p-accreditation-renewal-splash';
const SELECTOR_SPLASH_NEXT = '.p-accreditation-renewal-splash-next';

const SELECTOR_STEP = '.p-accreditation-renewal-step';
const SELECTOR_QUESTION = '.p-accreditation-renewal-questions';

const SELECTOR_BACK_BUTTON_WRAPPER = '.p-accreditation-renewal--exit-button';
const SELECTOR_EXIT_BUTTON = '.js-exit-accreditation-renewal';
const SELECTOR_EXIT_CONFIRM_MODAL = '.js-exit-accreditation-renewal-confirm';
const SELECTOR_EXIT_CONFIRM_YES = '.js-exit-accreditation-renewal-confirm-yes';
const SELECTOR_EXIT_CONFIRM_NO = '.js-exit-accreditation-renewal-confirm-no';

const IS_LOCAL = false;
const KEY_OBJECT = 'fieldName';
const VALUE_OBJECT = 'value';

class AccreditationRenewal {
	constructor() {
		this._wrapper = SelectorEngine.findOne(SELECTOR_WRAPPER);
		if (!this._wrapper) {
			return;
		}
		this.init();
	}

	get stepsData() {
		return this._stepsData;
	}

	set stepsData(_stepsData) {
		this._stepsData = _stepsData;
		if (_stepsData?.length > 0) {
			this.initiateFormGenerator(_stepsData);
		}
	}

	init() {
		this.modalElement = SelectorEngine.findOne(SELECTOR_MODAL, this._wrapper);
		this.modal = new Modal(this.modalElement, { backdrop: 'static' });
		this.modal.show();

		this.splash = SelectorEngine.findOne(SELECTOR_SPLASH, this._wrapper);
		this.splashNext = SelectorEngine.findOne(SELECTOR_SPLASH_NEXT, this.splash);

		this.step = SelectorEngine.findOne(SELECTOR_STEP, this._wrapper);
		this.questions = SelectorEngine.findOne(SELECTOR_QUESTION, this.step);

		this.exitButtonWrapper = SelectorEngine.findOne(SELECTOR_BACK_BUTTON_WRAPPER, this._wrapper);
		this.exitButton = SelectorEngine.findOne(SELECTOR_EXIT_BUTTON, this._wrapper);
		const exitConfirmModalHtml = SelectorEngine.findOne(SELECTOR_EXIT_CONFIRM_MODAL, this._wrapper);
		this.exitConfirmModal = new Modal(exitConfirmModalHtml, { backdrop: 'static' });

		this.exitConfirmYes = SelectorEngine.findOne(SELECTOR_EXIT_CONFIRM_YES, this._wrapper);
		this.exitConfirmNo = SelectorEngine.findOne(SELECTOR_EXIT_CONFIRM_NO, this._wrapper);
		this.accreditedApplyStatus = Manipulator.getDataAttribute(this._wrapper, 'apply-status');
		this.redirectUrl = Manipulator.getDataAttribute(this._wrapper, 'redirect-url');

		this.exitUrl = Manipulator.getDataAttribute(this._wrapper, 'exit-url');

		this.isRenewal = Manipulator.getDataAttribute(this._wrapper, 'is-renewal');

		this.initEventHandlers();
	}

	initEventHandlers() {
		EventHandler.on(this.splashNext, Events.Click, () => {
			this.splashNext.disabled = true;
			this.exitButtonWrapper.classList.add(ClassNames.Hidden);
			this.fetchSteps();
		});
		EventHandler.on(this.exitButton, Events.Click, () => {
			this.exitConfirmModal.show();
		});
		EventHandler.on(this.exitConfirmYes, Events.Click, () => {
			this.handleModalExit();
		});
		EventHandler.on(this.exitConfirmNo, Events.Click, () => {
			this.exitConfirmModal.hide();
		});
	}

	async fetchSteps() {
		startLoadingAnimation();
		try {
			// TODO CSA: this endpoint has not been confirmed yet
			let url = '/api/coachdetails/getaccreditationrenewalform';
			if (this.isRenewal) {
				url = '/api/coachdetails/getaccreditationrenewalformprefilled';
			}
			const response = await Request.get(
				url,
				{},
				{
					local: IS_LOCAL,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);
			const { Steps, error } = response;
			if (Steps) {
				this.stepsData = Steps;
			}

			if (!!error?.hasError) {
				throw error;
			}
		} catch (error) {
			this.splashNext.disabled = false
			console.log('error', error);

			if (error.hasError) {
				console.log('Error :', error);
			}
		} finally {
			stopLoadingAnimation();
		}
	}

	initiateFormGenerator(stepsData) {
		this.splash.classList.add(ClassNames.Hidden);
		this.step.classList.remove(ClassNames.Hidden);

		this.formGenerator = new FormGenerator(this.questions, {
			questions: stepsData,
			totalSteps: 8,
			hasSeparator: false,
			formType: FormTypes.StepForm,
			stepTitle: 'YOUR VENUES',
			themeColor: 'color-lta-blue',
			radioAnswerField: 'id',
			isBackButtonEnabled: true,
			isExitButtonEnabled: true,
			exitButtonHandler: () => {
				this.exitConfirmModal.show();
			},
			backButtonHandlerFirstStep: () => {
				this.splash.classList.remove(ClassNames.Hidden);
				this.step.classList.add(ClassNames.Hidden);
			}
		});

		this.formGenerator.topBackButton.hide();

		this.formGenerator.onStepChange((step) => {
			if(step !== 1) {
				this.formGenerator.topBackButton.show();
			} else {
				this.formGenerator.topBackButton.hide();
			}
			this.step.scrollTop = 0;
		});

		this.formGenerator.onSubmit((answers) => {
			const payload = this.prepareResponse(answers);
			this.saveData(payload);
		});
	}

	async saveData(payload) {
		startLoadingAnimation();
		try {
			const url = '/api/myaccreditationDetails/saveCoachAccreditationDetails';
			const apiResponse = await Request.put(url, payload, {
				local: IS_LOCAL,
				req: {
					apiMode: ApiMode.LtaError
				}
			});
			const { success, redirectURL } = apiResponse;
			if (success) {
				window.location.href = `${window.location.origin}${this.redirectUrl}`;
			}
		} catch (error) {
			console.error(error);
		} finally {
			stopLoadingAnimation();
		}
	}

	handleModalExit() {
		if (this.exitUrl != '') {
			window.location.href = this.exitUrl;
		} else {
			// Go back to the page that launched the modal
			history.back();
		}
	}

	prepareResponse(answers) {
		return {
			accreditationStatus: this.accreditedApplyStatus,
			profile: {
				myCurrentOfferings: {
					currentOfferings: this.getAnswerByFieldName(answers, 'myCurrentOfferings')?.map((item) => item.id)
				},
				myAffiliatedVenues: this.getAnswerByFieldName(answers, 'myAffiliatedVenues')?.map((item) => ({
					//id: item.operation && item.operation == 'add' ? null : item.id, //#24381
					id: item?.id,
					venue: item?.venue,
					operation: item?.operation,
					role: item?.role,
					address: item?.address
				}))
			},
			coachingDetails: {
				imparimentSpecificSessions: {
					disabilityTennisFlag: this.getAnswerByFieldName(answers, 'disabilityTennisFlag'),
					disabilityTennisCategory: this.getAnswerByFieldName(answers, 'disabilityTennisCategory')?.map(
						(item) => item.id
					)
				},
				myEmployment: {
					employmentStatus: this.getAnswerByFieldName(answers, 'employmentStatus'),
					limitedCompany: this.getAnswerByFieldName(answers, 'limitedCompany'),
					onCourtEmployees: this.getAnswerByFieldName(answers, 'onCourtEmployees')
				},
				mySchools: {
					schoolCoachFlag: this.getAnswerByFieldName(answers, 'schoolCoachFlag'),
					schoolTypes: this.getAnswerByFieldName(answers, 'schoolTypes')?.map((item) => item.id)
				},
				myPlayers: {
					playerAgeGroups: this.getAnswerByFieldName(answers, 'playerAgeGroups')?.map((item) => item.id),
					skills10AndUnder: this.getAnswerByFieldName(answers, 'skills10AndUnder')?.map((item) => item.id),
					skills11To14: this.getAnswerByFieldName(answers, 'skills11To14')?.map((item) => item.id),
					skills15To18: this.getAnswerByFieldName(answers, 'skills15To18')?.map((item) => item.id),
					skillsAdultsOrSeniors: this.getAnswerByFieldName(answers, 'skillsAdultsOrSeniors')?.map((item) => item.id)
				},
				futureOfferings: {
					programsAndInitiatives: this.getAnswerByFieldName(answers, 'futureOfferings')?.map((item) => item.id)
				},
				myCareer: {
					yearsOfExperience: this.getAnswerByFieldName(answers, 'yearsOfExperience'),
					hoursOfCoachingPerWeek: this.getAnswerByFieldName(answers, 'hoursOfCoachingPerWeek'),
					hoursOfOffCourtTasks: this.getAnswerByFieldName(answers, 'hoursOfOffCourtTasks'),
					consideringNextQualification: this.getAnswerByFieldName(answers, 'consideringNextQualification')
				}
			}
		};
	}

	getAnswerByFieldName(answers, filedName) {
		const question = this.formGenerator.questions.find((question) => question?.[KEY_OBJECT] === filedName);
		if (question) {
			return answers?.[question.step]?.[question?.id]?.value;
		}
		return null;
	}
}

const globalAccreditationRenewal = new AccreditationRenewal();
export default globalAccreditationRenewal;
